import { fromJS, List, Map } from 'immutable'
import { createReducer } from 'redux-immutablejs'

import { isAiConversationType } from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { CONVERSATION_STATE } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'
import type { Message } from '@lyrahealth-inc/shared-app-logic/src/models/messages/Messages'

import {
  ADD_MESSAGE,
  CLEAR_SELECTED_APPOINTMENT,
  CLEAR_SESSION_STORE,
  CLEAR_V2_HIGH_ALERT_TIMEOUT_DEADLINE,
  COMPLETE_PENDING_MESSAGE,
  DELETE_CUSTOM_NOTIFICATION_PREFERENCES,
  DESELECT_MESSENGER_PROVIDER,
  GET_CHILD_EPISODES,
  GET_CURRENT_EPISODES,
  GET_CUSTOM_NOTIFICATION_PREFERENCES,
  GET_FEEDBACK,
  GET_LT_APPOINTMENTS,
  GET_LT_APPOINTMENTS_BY_PROVIDER,
  GET_LT_APPOINTMENTS_FOR_CLIENT,
  GET_MESSAGE_CONVERSATION,
  GET_MESSAGES,
  GET_MESSAGES_REQUEST_FAILED,
  GET_MESSAGES_REQUESTED,
  GET_NOTIFICATION_PREFERENCES,
  GET_PROVIDERS,
  GET_VIDEO_CALL_TOKEN,
  HIDE_SESSION_CANCELLATION_MODAL,
  INCREMENT_UNREAD_MESSAGE_COUNT,
  LOCK_V2_HIGH_ALERT_CONVERSATION,
  MERGE_MESSAGE_CONVERSATIONS,
  PREPEND_MESSAGES,
  REMOVE_PENDING_MESSAGE,
  SAVE_MESSAGE_DRAFT,
  SELECT_MESSENGER_CONVERSATION,
  SELECT_MESSENGER_PROVIDER,
  SET_ACTIVE_LIVE_MSG_SESSION,
  SET_APPOINTMENT,
  SET_APPOINTMENT_RESCHEDULE,
  SET_AWAITING_AI_RESPONSE,
  SET_CONVERSATIONS_CLIENT,
  SET_CUSTOM_NOTIFICATION_PREFERENCES,
  SET_DASHBOARD_FETCHED,
  SET_EPISODES,
  SET_FIRST_APPOINTMENT_BOOKED,
  SET_INTAKE_FORM_OPEN,
  SET_JUST_SHOWED_PUSHED_NOTIFICATION_PROMPT,
  SET_LIVE_MSG_PROVIDER_ID_TO_OPEN,
  SET_LT_PROVIDER,
  SET_NOTIFICATION_PREFERENCES,
  SET_OPEN_ASSIGNMENT_ID,
  SET_OUTCOMES,
  SET_PROGRAMS,
  SET_SHOW_SESSION_NOTIFICATION_PROMPT,
  SET_V2_HIGH_ALERT_TIMEOUT_DEADLINE,
  SET_VIEWED_NOTIFICATION_MODAL,
  SHOW_SESSION_CANCELLATION_MODAL,
  SUBMIT_FEEDBACK,
  TOGGLE_LIVE_MSG_SESSION,
  TOGGLE_MESSAGES_POPOVER,
  TOGGLE_SHOW_LIVE_MSG_MODAL,
  TOGGLE_SHOWN_LIVE_MSG_MODAL,
  TOGGLE_VIDEO_SESSION_MODAL,
  UPDATE_FEEDBACK_RESPONSE,
  UPDATE_SELECTED_CONVERSATION_ID,
  UPDATE_UNREAD_MESSAGE_COUNT,
  UPDATE_V2_UNREAD_MESSAGE_COUNT,
  VIDEO_SESSION_SETTINGS_UPDATE,
  VIDEO_SESSION_STATUS_UPDATE,
} from './LyraTherapyConstants'
import assignmentsReducer from '../assignments/data/assignmentsReducer'

export const initialState = fromJS({
  assignments: {
    data: [],
    selectedIndex: undefined,
  },
  outcomes: {
    data: [],
  },
  appointments: fromJS([]),
  provider: {},
  programs: [],
  episodes: [],
  childEpisodes: {}, // episodes for user's children, keyed by child's lyraId
  messenger: {
    selectedProviderId: null,
    selectedConversationId: null,
    drafts: [],
    awaitingAiResponse: false,
    messageHistoryRequestIds: {},
    highAlertInactiveConversationIds: {},
    highAlertTimeoutDeadlines: {},
  },
  video: {
    sessionOpen: false,
    sessionInfo: {},
    showModal: false,
    settings: {},
    appointments: [], // these are scoped segregated from "all" appointments since over time, the full list could grow substantially while the "today's upcoming video appointments" will likely always stay much smaller
  },
  sessions: {
    appointments: [],
    appointment: {},
    episodes: {
      current: [],
    },
  },
  notifications: {
    preferences: {},
    customNotificationPreferences: {},
  },
  dashboardFetched: false,
  intakeFormOpen: false,
  openAssignmentId: undefined,
  firstAppointmentBooked: false,
  justShowedPushNotificationPrompt: false,
  viewedNotificationModal: false,
  showPopover: false,
  feedback: {},
})

// Common reducer shared across appointment endpoints
const appointmentSharedReducer = (
  $$state: Map<string, any>,
  action: {
    appointmentType?: string
    data?: any
  },
) => {
  if (action.appointmentType === 'video') {
    const activeAppts = action.data.filter((appt: { appointmentStatus: string }) => {
      return ['newAppt', 'rescheduled'].includes(appt.appointmentStatus)
    })
    $$state = $$state.setIn(['video', 'appointments'], fromJS(activeAppts))
  } else {
    const activeAppts = action.data.filter((appt: { appointmentStatus: string }) => {
      return ['newAppt', 'rescheduled'].includes(appt.appointmentStatus)
    })

    $$state = $$state.setIn(['video', 'appointments'], fromJS(activeAppts))
    $$state = $$state.set('appointments', fromJS(action.data))
  }

  return $$state
}

const incrementConversationUnreadMessageCount = ($$state: Map<string, any>, conversationId?: string) => {
  const conversations = $$state.getIn(['messenger', 'conversations'], List())
  const conversationIdx = conversations.findIndex(
    ($$conversation: Map<string, any>) => $$conversation.get('conversation_id') === conversationId,
  )
  if (conversationIdx === -1) {
    return $$state
  }

  const conversationType = $$state.getIn([
    'messenger',
    'conversations',
    conversationIdx,
    'conversation_attributes',
    'type',
  ])
  const unreadCountKey = isAiConversationType(conversationType)
    ? 'unread_ai_coach_messages_count'
    : 'unread_patient_messages_count'
  const unreadCountPath = ['messenger', 'conversations', conversationIdx, 'conversation_attributes', unreadCountKey]
  const currentUnreadCount = $$state.getIn(unreadCountPath, 0)
  return $$state.setIn(unreadCountPath, currentUnreadCount + 1)
}

export default createReducer(initialState, {
  ...assignmentsReducer,
  [GET_VIDEO_CALL_TOKEN]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['video', 'sessionInfo'], fromJS(action.data))
    return $$state
  },
  [SET_LT_PROVIDER]: ($$state: Map<string, any>, action: { data: LyraTherapyReducerAction }) => {
    $$state = $$state.set('provider', fromJS(action.data))
    return $$state
  },
  [SET_EPISODES]: ($$state: Map<string, any>, action: { data: LyraTherapyReducerAction }) => {
    $$state = $$state.set('episodes', fromJS(action.data))
    return $$state
  },
  [SET_PROGRAMS]: ($$state: Map<string, any>, action: { data: LyraTherapyReducerAction }) => {
    $$state = $$state.set('programs', fromJS(action.data))
    return $$state
  },
  [GET_LT_APPOINTMENTS]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    return appointmentSharedReducer($$state, action)
  },
  [GET_LT_APPOINTMENTS_BY_PROVIDER]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    return appointmentSharedReducer($$state, action)
  },
  [GET_LT_APPOINTMENTS_FOR_CLIENT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    return appointmentSharedReducer($$state, action)
  },
  [TOGGLE_VIDEO_SESSION_MODAL]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['video', 'showModal'], action.payload)
    return $$state
  },
  [VIDEO_SESSION_STATUS_UPDATE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['video', 'sessionOpen'], action.payload)
    return $$state
  },
  [VIDEO_SESSION_SETTINGS_UPDATE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['video', 'settings'], fromJS(action.payload))
    return $$state
  },
  [SET_APPOINTMENT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(
      ['sessions', 'appointment'],
      $$state
        .getIn(['sessions', 'appointment'])
        .mergeWith((oldVal: any, newVal: any) => newVal || oldVal, fromJS(action.appointment)),
    )
    return $$state
  },
  [SET_APPOINTMENT_RESCHEDULE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['sessions', 'appointmentReschedule'], fromJS(action.appointment))
    $$state = $$state.setIn(['sessions', 'appointmentReschedulePatientLyraId'], action.patientLyraId)
    return $$state
  },
  [CLEAR_SESSION_STORE]: ($$state: Map<string, any>) => {
    $$state = $$state.setIn(['sessions', 'appointments'], fromJS([]))
    $$state = $$state.setIn(['sessions', 'appointment'], fromJS({}))
    $$state = $$state.setIn(['sessions', 'appointmentReschedulePatientLyraId'], undefined)
    return $$state
  },
  [CLEAR_SELECTED_APPOINTMENT]: ($$state: Map<string, any>) => {
    $$state = $$state.setIn(['sessions', 'appointment'], fromJS({}))
    return $$state
  },
  [SHOW_SESSION_CANCELLATION_MODAL]: ($$state: Map<string, any>) => {
    $$state = $$state.setIn(['sessions', 'showModal'], true)
    return $$state
  },
  [HIDE_SESSION_CANCELLATION_MODAL]: ($$state: Map<string, any>) => {
    $$state = $$state.setIn(['sessions', 'showModal'], false)
    return $$state
  },
  [GET_NOTIFICATION_PREFERENCES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['notifications', 'preferences'], fromJS(action?.preferences?.data) || fromJS({}))
    return $$state
  },
  [SET_NOTIFICATION_PREFERENCES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['notifications', 'preferences'], fromJS(action.preferences))
    return $$state
  },
  [DELETE_CUSTOM_NOTIFICATION_PREFERENCES]: ($$state: Map<string, any>) => {
    $$state = $$state.setIn(['notifications', 'customNotificationPreferences'], fromJS({}))
    return $$state
  },
  [SET_CUSTOM_NOTIFICATION_PREFERENCES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['notifications', 'customNotificationPreferences'], fromJS(action.preferences))
    return $$state
  },
  [GET_CUSTOM_NOTIFICATION_PREFERENCES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(
      ['notifications', 'customNotificationPreferences'],
      fromJS(action?.preferences?.data) || fromJS({}),
    )
    return $$state
  },
  [SET_SHOW_SESSION_NOTIFICATION_PROMPT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['notifications', 'showSessionNotificationPrompt'], action.value)
    return $$state
  },
  [GET_MESSAGE_CONVERSATION]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'conversations'], fromJS(action.data))
    return $$state
  },
  [MERGE_MESSAGE_CONVERSATIONS]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const $$existingConversations = $$state.getIn(['messenger', 'conversations'], List())
    const isAiConversation = ($$conversation: Map<string, any>) =>
      isAiConversationType($$conversation.getIn(['conversation_attributes', 'type']))

    const $$providerConversations =
      action.providerConversations === undefined
        ? $$existingConversations.filterNot(isAiConversation)
        : fromJS(action.providerConversations)
    const $$aiCoachConversations =
      action.aiCoachConversations === undefined
        ? $$existingConversations.filter(isAiConversation)
        : fromJS(action.aiCoachConversations)
    const $$highAlertInactiveConversationIds = $$state.getIn(['messenger', 'highAlertInactiveConversationIds'], Map())
    const $$conversations = $$providerConversations
      .concat($$aiCoachConversations)
      .map(($$conversation: Map<string, any>) =>
        $$highAlertInactiveConversationIds.has($$conversation.get('conversation_id'))
          ? $$conversation.set('conversation_state', CONVERSATION_STATE.INACTIVE)
          : $$conversation,
      )
    const $$highAlertTimeoutDeadlines = $$state
      .getIn(['messenger', 'highAlertTimeoutDeadlines'], Map())
      .filterNot((_deadline: number, conversationId: string) =>
        $$conversations.some(
          ($$conversation: Map<string, any>) =>
            $$conversation.get('conversation_id') === conversationId &&
            $$conversation.get('conversation_state') === CONVERSATION_STATE.INACTIVE,
        ),
      )

    return $$state
      .setIn(['messenger', 'conversations'], $$conversations)
      .setIn(['messenger', 'highAlertTimeoutDeadlines'], $$highAlertTimeoutDeadlines)
  },
  [LOCK_V2_HIGH_ALERT_CONVERSATION]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const conversationIdx = $$state
      .getIn(['messenger', 'conversations'], List())
      .findIndex(($$conversation: Map<string, any>) => $$conversation.get('conversation_id') === action.conversationId)
    $$state = $$state
      .setIn(['messenger', 'highAlertInactiveConversationIds', action.conversationId], true)
      .deleteIn(['messenger', 'highAlertTimeoutDeadlines', action.conversationId])
    return conversationIdx === -1
      ? $$state
      : $$state.setIn(
          ['messenger', 'conversations', conversationIdx, 'conversation_state'],
          CONVERSATION_STATE.INACTIVE,
        )
  },
  [SET_V2_HIGH_ALERT_TIMEOUT_DEADLINE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    if (
      $$state.hasIn(['messenger', 'highAlertInactiveConversationIds', action.conversationId]) ||
      $$state.hasIn(['messenger', 'highAlertTimeoutDeadlines', action.conversationId])
    ) {
      return $$state
    }
    return $$state.setIn(['messenger', 'highAlertTimeoutDeadlines', action.conversationId], action.deadline)
  },
  [CLEAR_V2_HIGH_ALERT_TIMEOUT_DEADLINE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) =>
    $$state.deleteIn(['messenger', 'highAlertTimeoutDeadlines', action.conversationId]),
  [SELECT_MESSENGER_PROVIDER]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'selectedProviderId'], action.data)
    return $$state
  },
  [SELECT_MESSENGER_CONVERSATION]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'selectedConversationId'], action.data?.conversationId)
    $$state = $$state.setIn(['messenger', 'selectedProviderId'], action.data?.providerId)
    return $$state
  },
  [DESELECT_MESSENGER_PROVIDER]: ($$state: Map<string, any>) => {
    $$state = $$state.setIn(['messenger', 'selectedProviderId'], null)
    $$state = $$state.setIn(['messenger', 'selectedConversationId'], null)
    return $$state
  },
  [GET_MESSAGES_REQUESTED]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) =>
    $$state.setIn(['messenger', 'messageHistoryRequestIds', action.conversationId], action.requestId),
  [GET_MESSAGES_REQUEST_FAILED]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const requestIdPath = ['messenger', 'messageHistoryRequestIds', action.conversationId]
    return $$state.getIn(requestIdPath) === action.requestId ? $$state.deleteIn(requestIdPath) : $$state
  },
  [GET_MESSAGES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    if (action.requestId) {
      const requestIdPath = ['messenger', 'messageHistoryRequestIds', action.conversationId]
      if ($$state.getIn(requestIdPath) !== action.requestId) return $$state
      $$state = $$state.deleteIn(requestIdPath)
    }
    $$state = $$state.setIn(['messenger', 'messages', action.conversationId], fromJS(action.data))
    return $$state
  },
  [PREPEND_MESSAGES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const conversationMessagesPath = ['messenger', 'messages', action.conversationId, 'conversation_messages']
    const currentBefore = $$state.getIn(['messenger', 'messages', action.conversationId, 'before']) as
      | string
      | null
      | undefined
    if (action.requestedBefore !== currentBefore) return $$state

    const nextBefore = action.data?.before ?? null
    const hasAdvancingCursor = nextBefore !== null && nextBefore !== currentBefore
    const $$currentMessages = $$state.getIn(conversationMessagesPath, List()) as List<Map<string, any>>
    const knownMessageIds = new Set(
      $$currentMessages
        .map(($$message) => $$message?.get('message_id'))
        .filter(Boolean)
        .toArray(),
    )
    const olderMessages = (action.data?.messages ?? []).filter((message) => {
      if (knownMessageIds.has(message.message_id)) return false
      knownMessageIds.add(message.message_id)
      return true
    })

    return $$state
      .setIn(conversationMessagesPath, fromJS(olderMessages).concat($$currentMessages))
      .setIn(
        ['messenger', 'messages', action.conversationId, 'hasMore'],
        Boolean(action.data?.hasMore && hasAdvancingCursor),
      )
      .setIn(['messenger', 'messages', action.conversationId, 'before'], nextBefore)
  },
  [ADD_MESSAGE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const conversationId = action.conversationId || action?.data?.conversation_sid
    const $$conversationMessages = $$state.getIn(
      ['messenger', 'messages', conversationId, 'conversation_messages'],
      List(),
    )
    $$state = $$state.setIn(
      ['messenger', 'messages', conversationId, 'conversation_messages'],
      $$conversationMessages.push(fromJS(action.data)),
    )
    return $$state
  },
  [COMPLETE_PENDING_MESSAGE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    if (!action.conversationId || !action.pendingMessageId || !action.clientMessage || !action.assistantMessage) {
      return $$state
    }
    const messagesPath = ['messenger', 'messages', action.conversationId, 'conversation_messages']
    const $$conversationMessages = $$state.getIn(messagesPath, List())
    const pendingMessageIndex = $$conversationMessages.findIndex(
      ($$message: Map<string, any>) => $$message.get('message_id') === action.pendingMessageId,
    )
    if (pendingMessageIndex === -1 || $$conversationMessages.getIn([pendingMessageIndex, 'sendStatus']) !== 'sending') {
      return $$state
    }

    const $$clientMessage = fromJS(action.clientMessage)
    const $$assistantMessage = fromJS(action.assistantMessage)
    const $$completedMessages = $$conversationMessages
      .set(pendingMessageIndex, $$clientMessage)
      .insert(pendingMessageIndex + 1, $$assistantMessage)

    return incrementConversationUnreadMessageCount(
      $$state.setIn(messagesPath, $$completedMessages),
      action.conversationId,
    )
  },
  [REMOVE_PENDING_MESSAGE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    if (!action.conversationId || !action.pendingMessageId) {
      return $$state
    }
    const messagesPath = ['messenger', 'messages', action.conversationId, 'conversation_messages']
    const $$conversationMessages = $$state.getIn(messagesPath, List())
    const pendingMessageIndex = $$conversationMessages.findIndex(
      ($$message: Map<string, any>) => $$message.get('message_id') === action.pendingMessageId,
    )
    if (pendingMessageIndex === -1) {
      return $$state
    }

    return $$state.setIn(messagesPath, $$conversationMessages.delete(pendingMessageIndex))
  },
  [UPDATE_UNREAD_MESSAGE_COUNT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const conversationIdx = $$state
      .getIn(['messenger', 'conversations'])
      .findIndex(($$conversation: any) => $$conversation.get('conversation_id') === action.conversationId)
    $$state = $$state.setIn(
      ['messenger', 'conversations', conversationIdx, 'conversation_attributes'],
      fromJS(action.data),
    )
    return $$state
  },
  [UPDATE_V2_UNREAD_MESSAGE_COUNT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const conversations = $$state.getIn(['messenger', 'conversations'], List())
    const conversationIdx = conversations.findIndex(
      ($$conversation: Map<string, any>) => $$conversation.get('conversation_id') === action.conversationId,
    )
    if (conversationIdx === -1) {
      return $$state
    }

    const attributesPath = ['messenger', 'conversations', conversationIdx, 'conversation_attributes']
    const $$conversationAttributes = $$state.getIn(attributesPath, Map())
    return $$state.setIn(attributesPath, $$conversationAttributes.merge(fromJS(action.data)))
  },
  [INCREMENT_UNREAD_MESSAGE_COUNT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    return incrementConversationUnreadMessageCount($$state, action.conversationId)
  },
  [SET_CONVERSATIONS_CLIENT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'conversationsClient'], fromJS(action.data))
    return $$state
  },
  [UPDATE_SELECTED_CONVERSATION_ID]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const conversationIndex = $$state.getIn(['messenger', 'conversations']).findIndex(($$conversation: any) => {
      return $$conversation.get('conversation_id') === action?.data?.oldConversationId
    })
    const conversationMessages = $$state.getIn(['messenger', 'messages', action?.data?.oldConversationId])

    // Update the conversation_id to the new one.
    $$state = $$state.setIn(['selectedConversation', 'conversation_id'], action?.data?.newConversationId)
    $$state = $$state.setIn(['selectedConversation', 'conversation_state'], CONVERSATION_STATE.ACTIVE)
    $$state = $$state.setIn(
      ['messenger', 'conversations', conversationIndex, 'conversation_id'],
      action?.data?.newConversationId,
    )
    $$state = $$state.setIn(
      ['messenger', 'conversations', conversationIndex, 'conversation_state'],
      CONVERSATION_STATE.ACTIVE,
    )

    // Update the overall messages array that is in state
    $$state = $$state.setIn(['messenger', 'messages', action?.data?.newConversationId], conversationMessages)
    return $$state
  },
  [SAVE_MESSAGE_DRAFT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    if (action?.data?.conversationId) {
      const $$drafts = $$state.getIn(['messenger', 'drafts'])
      const draftIdx = $$drafts.findIndex(
        ($$draft: Map<string, any>) => $$draft.get('conversationId') === action?.data?.conversationId,
      )
      if (draftIdx === -1) {
        $$state = $$state.setIn(['messenger', 'drafts'], $$drafts.push(fromJS(action.data)))
      } else {
        $$state = $$state.setIn(['messenger', 'drafts', draftIdx], fromJS(action.data))
      }
    }
    return $$state
  },
  [GET_CURRENT_EPISODES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['sessions', 'episodes', 'current'], fromJS(action.data) || fromJS([]))
    return $$state
  },
  [SET_OUTCOMES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['outcomes', 'data'], fromJS(action?.data?.outcomes))
    return $$state
  },
  [GET_PROVIDERS]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['providers'], fromJS(action.data))
    return $$state
  },
  [GET_CHILD_EPISODES]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    if (action.data?.childLyraId) {
      $$state = $$state.setIn(['childEpisodes', action.data.childLyraId], fromJS(action.data.episodes))
    }
    return $$state
  },
  [SET_DASHBOARD_FETCHED]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.set('dashboardFetched', action.value)
    return $$state
  },
  [TOGGLE_LIVE_MSG_SESSION]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'inLiveMsgSession'], action.payload)
    return $$state
  },
  [TOGGLE_SHOWN_LIVE_MSG_MODAL]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'shownModal'], action.payload)
    $$state = $$state.setIn(['messenger', 'showModal'], true)
    $$state = $$state.setIn(['messenger', 'inLiveMsgSession'], true)
    return $$state
  },
  [SET_ACTIVE_LIVE_MSG_SESSION]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const { startsIn, appt } = action.payload
    $$state = $$state.setIn(['messenger', 'activeLiveMsgAppt'], fromJS(appt))
    $$state = $$state.setIn(['messenger', 'activeLiveMsgApptStartsIn'], startsIn)
    return $$state
  },
  [TOGGLE_SHOW_LIVE_MSG_MODAL]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'showModal'], action.payload)
    return $$state
  },
  [TOGGLE_MESSAGES_POPOVER]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'showPopover'], action.payload)
    return $$state
  },
  [SET_AWAITING_AI_RESPONSE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'awaitingAiResponse'], action.payload)
    return $$state
  },
  [SET_LIVE_MSG_PROVIDER_ID_TO_OPEN]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['messenger', 'liveMsgProviderIdToOpen'], action.payload)
    return $$state
  },
  [SET_INTAKE_FORM_OPEN]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['intakeFormOpen'], action.payload)
    return $$state
  },
  [SET_OPEN_ASSIGNMENT_ID]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['openAssignmentId'], action.payload)
    return $$state
  },
  [SET_FIRST_APPOINTMENT_BOOKED]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['firstAppointmentBooked'], action.payload)
    return $$state
  },
  [SET_JUST_SHOWED_PUSHED_NOTIFICATION_PROMPT]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['justShowedPushNotificationPrompt'], action.payload)
    return $$state
  },
  [SET_VIEWED_NOTIFICATION_MODAL]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['viewedNotificationModal'], action.payload)
    return $$state
  },
  [GET_FEEDBACK]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    $$state = $$state.setIn(['feedback', action.payload.entityType], action.payload.feedback)
    return $$state
  },
  [SUBMIT_FEEDBACK]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    const entityType = action?.data?.entity_type
    if (entityType) {
      const currentList = $$state.getIn(['feedback', entityType]) || []
      $$state = $$state.setIn(['feedback', entityType], [action.data, ...currentList])
    }
    return $$state
  },
  [UPDATE_FEEDBACK_RESPONSE]: ($$state: Map<string, any>, action: LyraTherapyReducerAction) => {
    // The PATCH response returns a FeedbackResponse object, not a full Feedback object
    // We need to find the parent Feedback by feedback_id and update its feedback_response array
    const updatedFeedbackResponse = action.data
    if (updatedFeedbackResponse?.feedback_id) {
      const feedbackState = $$state.get('feedback') || Map()
      let updated = false

      feedbackState.forEach((feedbackList: any, entityType: string) => {
        if (updated) return

        const currentList = feedbackList || []
        const updatedList = currentList.map((feedback: any) => {
          if (feedback.id === updatedFeedbackResponse.feedback_id) {
            updated = true
            return {
              ...feedback,
              feedback_response: feedback.feedback_response.map((fr: any) =>
                fr.id === updatedFeedbackResponse.id ? updatedFeedbackResponse : fr,
              ),
            }
          }
          return feedback
        })

        if (updated) {
          $$state = $$state.setIn(['feedback', entityType], updatedList)
        }
      })
    }
    return $$state
  },
})

type LyraTherapyReducerAction = {
  data?: {
    conversation_sid?: string
    oldConversationId?: string
    newConversationId?: string
    conversationId?: string
    providerId?: string
    outcomes?: {
      data: []
    }
    childLyraId?: string
    episodes?: Episode[]
    entity_type?: string
    entity_id?: string
    // FeedbackResponse fields (for UPDATE_FEEDBACK_RESPONSE)
    feedback_id?: string
    id?: string
    question?: string
    answer?: string
    messages?: Message[]
    hasMore?: boolean
    before?: string | null
  }
  payload?: any
  appointment?: object
  patientLyraId?: string
  preferences?: { data?: object }
  action?: {
    appointmentType?: string
    data?: any
  }
  conversationId?: string
  pendingMessageId?: string
  clientMessage?: Message
  assistantMessage?: Message
  requestedBefore?: string
  requestId?: string
  deadline?: number
  providerConversations?: object[]
  aiCoachConversations?: object[]
  value?: any
}
